import React, {useEffect, useMemo} from 'react';
import {Route, Routes, useLocation} from 'react-router-dom';
import Drawer, {DrawerProps} from '@mui/material/Drawer';
import SearchIcon from '@mui/icons-material/Search';
import HomeIcon from '@mui/icons-material/Home';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PeopleIcon from '@mui/icons-material/People';
import {styled, Typography, Box, Collapse, List} from '@mui/material';
import {useTheme} from '@mui/material/styles';
import {
  Add,
  FindInPage,
  Note,
  Settings,
  Assignment,
  OpenInNew,
  Folder,
  Groups,
  Support,
  ExpandMore,
  ExpandLess,
  NotificationAdd,
  BackupTable,
  NoteAdd,
  EventNote,
  PersonAdd,
  Schedule,
  CheckCircle,
  Cancel,
  ContactPhone,
  Today,
} from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {CalendarIcon} from '@mui/x-date-pickers-pro';
import AutoDeleteIcon from '@mui/icons-material/AutoDelete';
import packageJson from '../../../package.json';
import buildDate from '../../environments/buildDate.json';
import AccountProvider, {useAccountContext} from '../../contexts/AccountContext';
import ACCOUNT_TAB from '../../constants/AccountTab';
import {getEnvironment} from '../../utils/envTest';
import {useDjangoContext} from '../../contexts/useAuthContext';
import {FeatureBoundary, useFeaturePermission} from '../../contexts/FeatureBoundary';
import {getCuroURL} from '../../utils/getCuroURL';
import {ReviewContextProvider, useReviewContext} from '../../contexts/ReviewContext';
import useImagoSSO from '../../hooks/useImagoSSO';
import {BusinessUnitEnum, GlobalPermissionEnum} from '../../schema-types/globalTypes';
import {newUTCDate} from '../../utils/newUTCDate';
import MenuItem from '../MenuItem/MenuItem';
import ReviewMenuItem from '../ReviewsMenuItem/ReviewsMenuItem';
import {useSideMenuStyles} from './styles';
import themeTheme from '../../theme/Theme';
import ManageCuroDialog from '../Admin/ManageCURO';
import {ManageProfileSideMenu} from '../Header/Header';
import {ANNUAL_REVIEWS_NEW} from '../../constants/Routes';
import {getSentryReplayAccountUrl} from '../Header/DebugModal';
import {useRecommendContext} from '../../features/recommendations/CreateRecommend/CreateRecommendHelpers';
import {useQueryParams} from '../../hooks/useQueryParams';
import useOVRecommendDetails from '../../hooks/useOVRecommendDetails';
import validateUUID from '../../utils/validateUUID';
import {useRequirementsDentistOrBAU} from '../../hooks/useRequirementsDentistOrBAU';
import {useDebugContext} from '../../contexts/DebugProvider';

export const Header = styled('div')(({theme}) => ({
  color: theme.palette.primary.main,
  fontSize: 14,
  marginBottom: 2,
  fontWeight: 'bold',
  marginRight: theme.spacing(0.5),
  paddingLeft: theme.spacing(1),
}));

export const ReviewSideMenu: React.FC = () => {
  const {account} = useAccountContext();
  const {djangoUser} = useDjangoContext();
  const {reviewId, reviewDetails} = useReviewContext();
  const {pathname} = useLocation();
  const theme = useTheme();
  const hasDentistBuilderPerm = useFeaturePermission([
    GlobalPermissionEnum.can_access_dentist_recommendation_builder,
  ]);
  if (!reviewDetails) {
    return null;
  }

  const contacts = reviewDetails.contacts?.length
    ? reviewDetails.contacts
    : account?.contacts || [];

  const reviewJourneyBase = `/account/${account!.id}/review-journey/${reviewDetails.id}`;
  const requirementLinksElement = (
    <>
      <MenuItem
        href={`${reviewJourneyBase}/requirements`}
        icon={Assignment}
        label="Requirements"
        pathname={pathname}
      />
      {hasDentistBuilderPerm && (
        <MenuItem
          href={`${reviewJourneyBase}/${ACCOUNT_TAB.NEW_INSTRUCTIONS}`}
          icon={FindInPage}
          label="Builder"
          pathname={pathname}
        />
      )}
    </>
  );

  return (
    <>
      <MenuItem
        href={reviewJourneyBase}
        icon={Note}
        label={reviewDetails.name?.slice(0, 20) || 'Current review'}
        pathname={pathname}
      />
      <div
        style={{
          paddingLeft: theme.spacing(1.5),
        }}
      >
        <Routes>
          <Route path="/recommends-builder" element={requirementLinksElement} />
          <Route path="/requirements" element={requirementLinksElement} />
          <Route
            path={`/${ACCOUNT_TAB.NEW_INSTRUCTIONS}`}
            element={requirementLinksElement}
          />
          <Route path="/builder" element={requirementLinksElement} />
        </Routes>
        {reviewDetails.curoId ? (
          <FeatureBoundary
            features={[GlobalPermissionEnum.can_access_curo_FE]}
            showErrorIfDenied={false}
          >
            <MenuItem
              href={getCuroURL(djangoUser, 'review', reviewDetails.curoId)!}
              external
              icon={OpenInNew}
              label="Open review in curo"
              pathname={pathname}
            />
          </FeatureBoundary>
        ) : null}
        <FeatureBoundary
          features={[GlobalPermissionEnum.can_access_curo_FE]}
          showErrorIfDenied={false}
        >
          {contacts.map((contact) =>
            contact.curoId ? (
              <MenuItem
                key={contact.id}
                href={getCuroURL(djangoUser, 'contact', contact.curoId)!}
                external
                icon={OpenInNew}
                label={`Open ${contact.simplifiedName || contact.name} in curo`}
                pathname={pathname}
              />
            ) : null
          )}
        </FeatureBoundary>
      </div>
    </>
  );
};

export const ReviewSideMenuSPPDA: React.FC = () => {
  const {account} = useAccountContext();
  const {djangoUser} = useDjangoContext();
  const {reviewDetails} = useReviewContext();
  const {pathname} = useLocation();
  const theme = useTheme();

  const query = useQueryParams();
  const ovrecommendId = query.get('ovrecommendId');
  const {queryGetOVRecommendationDetails, ovRecommendationData} = useOVRecommendDetails();
  const {recommendsGridUrl} = useRequirementsDentistOrBAU();

  useEffect(() => {
    if (!ovrecommendId) {
      return;
    }
    queryGetOVRecommendationDetails({
      variables: {
        ovrecommendId: validateUUID(ovrecommendId)!,
      },
      fetchPolicy: 'network-only',
    });
  }, [ovrecommendId]);

  if (!reviewDetails) {
    return null;
  }

  return (
    <>
      <MenuItem
        href={`/account/${account!.id}/${recommendsGridUrl}`}
        icon={Note}
        label={reviewDetails.name?.slice(0, 20) || 'Current review'}
        pathname={pathname}
      />
      <div
        style={{
          paddingLeft: theme.spacing(1.5),
        }}
      >
        <FeatureBoundary
          features={[GlobalPermissionEnum.can_access_curo_FE]}
          showErrorIfDenied={false}
        >
          {ovRecommendationData?.recommends?.map((rec) => {
            return (
              <MenuItem
                key={rec.id}
                href={getCuroURL(djangoUser, 'recommendation', String(rec.curoId))!}
                external
                icon={OpenInNew}
                label={`Open instruction: ${rec.advice} in curo`}
                pathname={pathname}
              />
            );
          })}
        </FeatureBoundary>
      </div>
    </>
  );
};

export const AccountSideMenu: React.FC<{
  handleChange: React.Dispatch<React.SetStateAction<ExpandedPanel>>;
  expanded: boolean;
}> = ({handleChange, expanded}) => {
  const {pathname} = useLocation();
  const {account, accountType} = useAccountContext();
  const {djangoUser} = useDjangoContext();
  const theme = useTheme();
  const hasDentistBuilderPerm = useFeaturePermission([
    GlobalPermissionEnum.can_access_dentist_recommendation_builder,
  ]);
  const {debug} = useDebugContext();
  if (!account) {
    return null;
  }
  const sentryUrl = account.name ? getSentryReplayAccountUrl(account.name) : null;
  return (
    <div
      style={{
        background: theme.palette.grey[100],
        marginTop: theme.spacing(2),
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(2),
      }}
    >
      <div
        role="presentation"
        style={{
          display: 'flex',
          cursor: 'pointer',
        }}
        onClick={() => handleChange((e) => (e === 'account' ? null : 'account'))}
      >
        <Header sx={{marginLeft: '0.5rem', fontSize: 12}}>{account.name}</Header>
        <Box ml="auto">{expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}</Box>
      </div>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Routes>
          <Route
            path="/review-journey/:reviewId/*"
            element={
              <FeatureBoundary
                features={[GlobalPermissionEnum.can_access_ehons_notes_goals]}
                showErrorIfDenied={false}
              >
                <ReviewContextProvider hideLoading>
                  <ReviewSideMenu />
                </ReviewContextProvider>
              </FeatureBoundary>
            }
          />
          <Route
            path="/recommends-builder"
            element={
              <FeatureBoundary
                features={[GlobalPermissionEnum.can_access_instructions_FE]}
                showErrorIfDenied
              >
                <ReviewContextProvider hideLoading>
                  <ReviewSideMenuSPPDA />
                </ReviewContextProvider>
              </FeatureBoundary>
            }
          />
          {hasDentistBuilderPerm && (
            <Route
              path="/builder"
              element={
                <FeatureBoundary
                  features={[
                    GlobalPermissionEnum.can_access_dentist_recommendation_builder,
                  ]}
                  showErrorIfDenied
                >
                  <ReviewContextProvider hideLoading>
                    <ReviewSideMenuSPPDA />
                  </ReviewContextProvider>
                </FeatureBoundary>
              }
            />
          )}
        </Routes>

        <MenuItem
          href={`/account/${account.id}/${ACCOUNT_TAB.OVERVIEW}`}
          icon={Settings}
          label="Overview"
          pathname={pathname}
        />
        {accountType === BusinessUnitEnum.DA ? (
          <MenuItem
            href={`/account/${account.id}/${ACCOUNT_TAB.INSTRUCTIONS}`}
            icon={FindInPage}
            label="Instructions"
            pathname={pathname}
          />
        ) : (
          <MenuItem
            href={`/account/${account.id}/${ACCOUNT_TAB.REVIEWS}`}
            icon={FindInPage}
            label="Account Reviews"
            pathname={pathname}
          />
        )}
        {!pathname.includes('/review-journey/') && (
          <FeatureBoundary
            features={[GlobalPermissionEnum.can_access_dentist_recommendation_builder]}
            showErrorIfDenied={false}
          >
            <MenuItem
              href={`/account/${account.id}/${ACCOUNT_TAB.BUILDER}`}
              icon={FindInPage}
              label="Builder"
              pathname={pathname}
            />
          </FeatureBoundary>
        )}

        <FeatureBoundary
          features={[GlobalPermissionEnum.can_access_curo_FE]}
          showErrorIfDenied={false}
        >
          <MenuItem
            href={getCuroURL(djangoUser, 'account', account.curoId)!}
            external
            icon={OpenInNew}
            label="Open account in curo"
            pathname={pathname}
          />
        </FeatureBoundary>
        <FeatureBoundary
          features={[GlobalPermissionEnum.can_access_admin_features]}
          showErrorIfDenied={false}
        >
          {sentryUrl ? (
            <MenuItem
              href={sentryUrl}
              external
              icon={OpenInNew}
              label="Open account in Sentry"
              pathname={pathname}
            />
          ) : null}
        </FeatureBoundary>
        {debug.forms && (
          <FeatureBoundary
            features={[GlobalPermissionEnum.can_restore_admin_permission]}
            showErrorIfDenied={false}
          >
            <FeatureBoundary
              features={[GlobalPermissionEnum.can_access_dentist_recommendation_builder]}
              showErrorIfDenied={false}
            >
              <MenuItem
                href={`/test/test-any-open-review/${account.curoId}?goTo=builder`}
                icon={Add}
                external
                label="Debug: Create builder"
                pathname={pathname}
              />
            </FeatureBoundary>
          </FeatureBoundary>
        )}
      </Collapse>
    </div>
  );
};

export type ExpandedPanel = 'admin' | 'reviews' | 'account' | 'annual-reviews' | null;

export const AnnualReviewsSideMenu: React.FC<{
  pathname: string;
  expanded: boolean;
  handleChange: React.Dispatch<React.SetStateAction<ExpandedPanel>>;
}> = ({pathname, expanded, handleChange}) => {
  const classes = useSideMenuStyles();

  const annualReviewsMenuItems = [
    {href: `/${ANNUAL_REVIEWS_NEW}/dashboard`, icon: EventNote, label: 'Dashboard'},
    {href: `/${ANNUAL_REVIEWS_NEW}/invite`, icon: PersonAdd, label: 'Invite'},
    {href: `/${ANNUAL_REVIEWS_NEW}/invited`, icon: Schedule, label: 'Invited'},
    {href: `/${ANNUAL_REVIEWS_NEW}/scheduled`, icon: CalendarIcon, label: 'Booked For'},
    {href: `/${ANNUAL_REVIEWS_NEW}/today`, icon: Today, label: 'Today'},
    {href: `/${ANNUAL_REVIEWS_NEW}/completed`, icon: CheckCircle, label: 'Completed'},
    {href: `/${ANNUAL_REVIEWS_NEW}/declined`, icon: Cancel, label: 'Declined'},
    {
      href: `/${ANNUAL_REVIEWS_NEW}/uncontactable`,
      icon: ContactPhone,
      label: 'Uncontactable',
    },
  ];

  return (
    <>
      <Box
        onClick={() =>
          handleChange((e) => (e === ANNUAL_REVIEWS_NEW ? null : ANNUAL_REVIEWS_NEW))
        }
        role="button"
        tabIndex={0}
        style={{
          ...classes.accordionSummary,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
        }}
      >
        <Header sx={{fontSize: 12}}>Annual Reviews</Header>
        {expanded ? <ExpandLess /> : <ExpandMore />}
      </Box>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          {annualReviewsMenuItems.map((item) => (
            <MenuItem
              key={item.label}
              href={item.href}
              icon={item.icon}
              label={item.label}
              pathname={pathname}
            />
          ))}
        </List>
      </Collapse>
    </>
  );
};

export const sideMenuWidth = 200;

export const SideMenu: React.FC<DrawerProps> = () => {
  const {pathname} = useLocation();
  const [expanded, setExpanded] = React.useState<ExpandedPanel>(() => {
    if (pathname.startsWith(`/${ANNUAL_REVIEWS_NEW}`)) {
      return ANNUAL_REVIEWS_NEW;
    }
    if (pathname.startsWith('/account')) {
      return 'account';
    }
    if (pathname.startsWith('/admin')) {
      return 'admin';
    }
    return null;
  });
  const {imagoLink, imagoLoading, imagoError, getImagoLink} = useImagoSSO();
  const envName = useMemo(() => {
    return getEnvironment();
  }, []);

  return (
    <Drawer
      open
      className="sidemenu"
      variant="persistent"
      PaperProps={{
        sx: {
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100% - 64px)',
          paddingTop: themeTheme.spacing(1),
          top: '64px !important',
          backgroundColor: themeTheme.palette.background.paper,
          width: sideMenuWidth,
          maxWidth: sideMenuWidth,
          [themeTheme.breakpoints.down('sm')]: {
            top: '56px',
            width: '100%',
            maxWidth: 'none',
          },
        },
      }}
    >
      <MenuItem
        href={envName === 'PROD' ? '/' : '/home'}
        icon={HomeIcon}
        label="Home"
        pathname={pathname}
      />
      <MenuItem
        href={envName === 'PROD' ? '/search' : '/'}
        icon={SearchIcon}
        label="Search"
        pathname={pathname}
      />
      <FeatureBoundary
        features={[GlobalPermissionEnum.can_access_client_creator]}
        showErrorIfDenied={false}
      >
        <MenuItem
          href="/add-account"
          icon={Add}
          label="Create client"
          pathname={pathname}
        />
      </FeatureBoundary>
      <FeatureBoundary
        features={[GlobalPermissionEnum.can_access_imago]}
        showErrorIfDenied={false}
      >
        <div
          onMouseEnter={() => {
            if (imagoLink) {
              return;
            }
            getImagoLink();
          }}
        >
          <MenuItem
            href={imagoLink || ''}
            external
            errorMessage={imagoError}
            icon={OpenInNew}
            label={imagoLoading ? 'Loading...' : 'Create illustration'}
            pathname=""
            disabled={imagoLoading}
          />
        </div>
      </FeatureBoundary>
      <FeatureBoundary
        features={[GlobalPermissionEnum.can_access_documents]}
        showErrorIfDenied={false}
      >
        <MenuItem href="/documents" icon={Folder} label="Library" pathname={pathname} />
      </FeatureBoundary>
      <Box pt={1} />
      <FeatureBoundary
        features={[GlobalPermissionEnum.can_access_lead_creator]}
        showErrorIfDenied={false}
      >
        {envName !== 'PROD' ? (
          <MenuItem href="/leads/basic" icon={Add} label="Add lead" pathname={pathname} />
        ) : null}
      </FeatureBoundary>
      <FeatureBoundary
        features={[GlobalPermissionEnum.can_access_bulk_accounts]}
        showErrorIfDenied={false}
      >
        <MenuItem
          href="/bulk-admin"
          icon={Groups}
          label="Bulk Admin"
          pathname={pathname}
        />
      </FeatureBoundary>
      <FeatureBoundary
        features={[GlobalPermissionEnum.can_access_annual_reviews]}
        showErrorIfDenied={false}
      >
        <AnnualReviewsSideMenu
          pathname={pathname}
          expanded={expanded === ANNUAL_REVIEWS_NEW}
          handleChange={setExpanded}
        />
      </FeatureBoundary>
      <FeatureBoundary
        features={[GlobalPermissionEnum.can_access_report_pack]}
        showErrorIfDenied={false}
      >
        <MenuItem
          href="/report-pack-dashboard"
          icon={BackupTable}
          label="Reports"
          pathname={pathname}
        />
      </FeatureBoundary>
      <FeatureBoundary
        showErrorIfDenied={false}
        features={[
          GlobalPermissionEnum.can_access_admin_features,
          GlobalPermissionEnum.can_restore_admin_permission,
        ]}
      >
        <AdminSideMenu
          pathname={pathname}
          expanded={expanded === 'admin'}
          handleChange={setExpanded}
        />
      </FeatureBoundary>
      <Box mt={2} />
      <FeatureBoundary
        features={[GlobalPermissionEnum.can_access_ehons_notes_goals]}
        showErrorIfDenied={false}
      >
        <ReviewMenuItem
          pathname={pathname}
          expanded={expanded === 'reviews'}
          handleChange={setExpanded}
        />
      </FeatureBoundary>
      <Routes>
        <Route
          path="/account/:accountId/*"
          element={
            <AccountProvider makeEmptyIfNoAccount>
              <AccountSideMenu
                expanded={expanded === 'account'}
                handleChange={setExpanded}
              />
            </AccountProvider>
          }
        />
      </Routes>
      {packageJson?.version && (
        <Box mt="auto" display="flex" flexDirection="column">
          <FeatureBoundary
            features={[GlobalPermissionEnum.can_access_sfp_support_FE]}
            showErrorIfDenied={false}
          >
            <MenuItem
              href="https://saltus.freshdesk.com/support/solutions"
              icon={Support}
              label="Need help? Access OneView Support"
              pathname={pathname}
              external
              boldFont
            />
          </FeatureBoundary>
          <FeatureBoundary
            features={[GlobalPermissionEnum.can_access_spp_support_FE]}
            showErrorIfDenied={false}
          >
            <MenuItem
              href="https://saltuspartnersllp.freshservice.com/support/home"
              icon={Support}
              label="Contact Partnership Support"
              pathname={pathname}
              external
              boldFont
            />
          </FeatureBoundary>
          <Box pl={1} mr={1} display="flex" flexDirection="column">
            <Typography color="black" variant="caption">
              version: {packageJson?.version}
            </Typography>
            <Typography variant="caption">
              date: {newUTCDate(buildDate.date).toLocaleString()}
            </Typography>
            <ManageProfileSideMenu />
          </Box>
        </Box>
      )}
    </Drawer>
  );
};

export const AdminSideMenu: React.FC<{
  pathname: string;
  expanded: boolean;
  handleChange: React.Dispatch<React.SetStateAction<ExpandedPanel>>;
}> = ({pathname, expanded, handleChange}) => {
  const classes = useSideMenuStyles();
  const [curoDialogOpen, setCuroDialogOpen] = React.useState(false);
  const {djangoUser} = useDjangoContext();
  return (
    <>
      <ManageCuroDialog
        open={curoDialogOpen}
        handleClose={() => setCuroDialogOpen(false)}
      />
      <Box
        onClick={() => handleChange((e) => (e === 'admin' ? null : 'admin'))}
        role="button"
        tabIndex={0}
        style={{
          ...classes.accordionSummary,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
        }}
      >
        <Header sx={{fontSize: 12}}>Admin</Header>
        {expanded ? <ExpandLess /> : <ExpandMore />}
      </Box>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          <MenuItem
            href={null}
            onClick={() => setCuroDialogOpen(true)}
            icon={AdminPanelSettingsIcon}
            label="Manage CURO"
            pathname={pathname}
          />
          <MenuItem
            href="/permissions-audit"
            icon={AdminPanelSettingsIcon}
            label="Permissions"
            pathname={pathname}
          />
          <MenuItem
            href="/audit"
            icon={AdminPanelSettingsIcon}
            label="Audit Entity History"
            pathname={pathname}
          />
          <MenuItem
            href="/identity"
            icon={AdminPanelSettingsIcon}
            label="Manage Access"
            pathname={pathname}
          />

          <MenuItem
            href="/soft-delete"
            icon={AutoDeleteIcon}
            label="Soft Deleted Entities"
            pathname={pathname}
          />
          <MenuItem
            href="/global-notifications"
            icon={NotificationAdd}
            label="Manage Notifications"
            pathname={pathname}
          />
          <FeatureBoundary
            showErrorIfDenied={false}
            features={[GlobalPermissionEnum.can_access_admin_features]}
          >
            <MenuItem
              href="/manage-clients"
              icon={PeopleIcon}
              label="Delete Account/Contact"
              pathname={pathname}
            />
          </FeatureBoundary>
          <FeatureBoundary
            showErrorIfDenied={false}
            features={[GlobalPermissionEnum.can_access_report_pack]}
          >
            <MenuItem
              href="/add-generic-document"
              icon={NoteAdd}
              label="Manage Disclosures"
              pathname={pathname}
            />
          </FeatureBoundary>
          <FeatureBoundary
            features={[GlobalPermissionEnum.can_access_curo_FE]}
            showErrorIfDenied={false}
          >
            <MenuItem
              href={getCuroURL(djangoUser, 'home', null)!}
              external
              icon={OpenInNew}
              label="Open CURO"
              pathname={pathname}
            />
          </FeatureBoundary>
        </List>
      </Collapse>
    </>
  );
};

export default SideMenu;
